/**
 * Medienplätze für die Stimmungen des Abends.
 *
 * Die Spiellogik kennt nur die Spielphase. Welches Bild und welche Musik dazugehören, steht
 * ausschließlich hier. Zum Austauschen genügt es, die Datei im Ordner `public/` zu ersetzen –
 * am Programm ändert sich nichts.
 *
 * Fehlt eine Datei, fällt die Anzeige auf den gestalteten Farbverlauf zurück und es bleibt still.
 * Es bricht also nichts, solange noch keine Medien hinterlegt sind.
 */
export type Ambience = 'START' | 'DAY' | 'DUSK' | 'NIGHT';

export interface MediaSlot {
  /** Hintergrundbild, das über den Farbverlauf gelegt wird. */
  image: string;
  /** Musik, die in Schleife läuft. */
  music: string | null;
  label: string;
}

/** Versionskennung: hochzählen, wenn eine Datei ersetzt wurde und sofort ankommen soll. */
const MEDIA_VERSION = 5;

export const MEDIA: Record<Ambience, MediaSlot> = {
  START: {
    image: `/backgrounds/start.jpg?v=${MEDIA_VERSION}`,
    music: `/music/Lobby.mp3?v=${MEDIA_VERSION}`,
    label: 'Vorbereitung',
  },
  DAY: {
    image: `/backgrounds/day.jpg?v=${MEDIA_VERSION}`,
    music: `/music/music_day.mp3?v=${MEDIA_VERSION}`,
    label: 'Tag',
  },
  DUSK: {
    image: `/backgrounds/dusk.jpg?v=${MEDIA_VERSION}`,
    music: `/music/music_voting.mp3?v=${MEDIA_VERSION}`,
    label: 'Abstimmung',
  },
  NIGHT: {
    image: `/backgrounds/night.jpg?v=${MEDIA_VERSION}`,
    music: `/music/music_night.mp3?v=${MEDIA_VERSION}`,
    label: 'Nacht',
  },
};

/** Siegesbilder. Fehlt eines, bleibt allein die Farbe der Feier stehen. */
export function winImage(winner: string): string {
  return `/wins/W_${winner}.jpg?v=${MEDIA_VERSION}`;
}

/** Dauer der Überblendung zwischen den Stimmungen, für Bild und Musik gleichermaßen. */
export const CROSSFADE_MS = 2000;
/** Dauer, mit der ein Bild erscheint – etwa der Hintergrund der Siegesverkündung. */
export const FADE_IN_MS = 2000;

/** Solange die Runde vorbereitet wird, läuft das Startbild. */
const PREPARING = new Set(['LOBBY', 'ROLE_SETUP', 'MAYOR_ELECTION', 'GAME_START']);
const VOTING = new Set(['DAY_VOTE', 'VOTE_REVEAL', 'MAYOR_TIEBREAK', 'DAY_VOTE_RESOLUTION']);

/**
 * Ordnet einer Spielphase die Stimmung zu: vor dem Beginn das Startbild, nachts der Nachthimmel,
 * zur Abstimmung die Dämmerung, sonst der Tag. Mehr weiß die Anzeige nicht über das Spiel.
 */
export function ambienceFor(layout: 'LOBBY' | 'DAY' | 'NIGHT' | 'END', phase: string): Ambience {
  if (PREPARING.has(phase)) return 'START';
  if (layout === 'NIGHT') return 'NIGHT';
  return VOTING.has(phase) ? 'DUSK' : 'DAY';
}
